<script module lang="ts">
	export type OptionValue = string | number | boolean;

	export interface Option<V = OptionValue> {
		value: V;
		label: string;
		count?: number;
		disabled?: boolean;
		hidden?: boolean;
	}
</script>

<script lang="ts" generics="T extends OptionValue = string">
	import type { Snippet } from 'svelte';

	interface Props {
		options: Option<T>[];
		value: T;
		variant?: 'default' | 'surface' | 'glass';
		className?: string;
		onchange?: (value: T) => void;
		children?: Snippet<[Option<T>]>;
	}

	let {
		options,
		value = $bindable(),
		variant = 'default',
		className = '',
		onchange,
		children
	}: Props = $props();

	let isSurface = $derived(variant === 'surface');
	let isGlass = $derived(variant === 'glass');
</script>

<div
	class={isGlass
		? `flex items-center gap-1.5 p-1 bg-surface/10 dark:bg-surface-dark/20 border border-surface/25 dark:border-surface-dark/40 rounded-xl text-xs font-semibold w-full backdrop-blur-xs ${className}`
		: isSurface
			? `flex items-center gap-1.5 p-1 bg-surface-on/5 dark:bg-surface-dark-on/5 rounded-xl text-xs font-semibold w-full ${className}`
			: `flex items-center rounded-lg p-0.5 border border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-surface-dark max-w-full w-fit ${className}`}
>
	{#each options as option (option.value)}
		{#if !option.hidden}
			<button
				type="button"
				disabled={option.disabled}
				title={option.label}
				class="transition-all duration-200 min-w-0 flex items-center justify-center gap-1.5 {isGlass
					? `flex-1 py-2 px-3 rounded-lg text-center cursor-pointer ${value === option.value ? 'bg-surface/30 dark:bg-surface-dark/50 text-surface font-bold shadow-xs border border-surface/30' : 'opacity-70 hover:opacity-100 text-surface hover:bg-surface/10'}`
					: isSurface
						? `flex-1 py-2 px-3 rounded-lg text-center cursor-pointer ${value === option.value ? 'bg-surface dark:bg-surface-dark text-surface-on dark:text-surface-dark-on font-bold shadow-2xs' : 'opacity-60 hover:opacity-100 text-surface-on dark:text-surface-dark-on'}`
						: `px-3 py-1 text-sm rounded-md ${value === option.value ? 'bg-white text-surface-on shadow-xs font-semibold dark:bg-white/10 dark:text-white' : 'text-surface-on/50 hover:text-surface-on dark:text-surface-dark-on/60 dark:hover:text-surface-dark-on font-medium'}`} {option.disabled
					? 'opacity-50 cursor-not-allowed'
					: ''}"
				onclick={() => {
					if (!option.disabled) {
						value = option.value;
						onchange?.(option.value);
					}
				}}
			>
				{#if children}
					{@render children(option)}
				{:else}
					<span class="truncate min-w-0">{option.label}</span>
					{#if option.count !== undefined}
						<span
							class="shrink-0 text-2xs font-semibold px-1.5 py-0.2 rounded-full transition-colors {value ===
							option.value
								? 'bg-surface-on/10 dark:bg-white/20 text-surface-on dark:text-white'
								: 'bg-surface-on/5 dark:bg-white/10 text-surface-on/60 dark:text-surface-dark-on/60'}"
						>
							{option.count}
						</span>
					{/if}
				{/if}
			</button>
		{/if}
	{/each}
</div>
